/**
 * CommAuditLog — read-only table of every outbound email / WhatsApp / SMS dispatched.
 * Works for both tenant and platform scope (just switch the endpoint).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageCircle, Send, AlertCircle, CheckCircle2, RotateCcw, Loader2 } from "lucide-react";
import { fmtDateTime } from "@/lib/time";

const ENDPOINT = {
    tenant:   "/comm-audit",
    platform: "/admin/comm-audit",
};
const RETRY_ENDPOINT = {
    tenant:   (id) => `/comm-audit/${id}/retry`,
    platform: (id) => `/admin/comm-audit/${id}/retry`,
};
const BULK_RETRY_ENDPOINT = {
    tenant:   "/comm-audit/retry-bulk",
    platform: "/admin/comm-audit/retry-bulk",
};

const CH_ICON = { email: Mail, whatsapp: MessageCircle, sms: Send, telegram: Send };

export default function CommAuditLog({ scope = "tenant" }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ channel: "", status: "" });
    const [expanded, setExpanded] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [bulkRunning, setBulkRunning] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.channel) params.channel = filters.channel;
            if (filters.status)  params.status  = filters.status;
            const { data } = await api.get(ENDPOINT[scope], { params });
            setRows(data);
        } catch (e) {
            // Tenant-side may hit 403 if super-admin has disabled visibility
            const msg = e?.response?.data?.detail || apiError(e);
            if (e?.response?.status === 403) toast.error(msg, { duration: 6000 });
            else toast.error(apiError(e));
            setRows([]);
        } finally { setLoading(false); }
    }, [scope, filters]);
    useEffect(() => { load(); }, [load]);
    // Reset selection whenever the filter set changes (so we never act on stale ids)
    useEffect(() => { setSelected(new Set()); }, [filters]);

    const retry = async (id, e) => {
        e?.stopPropagation();
        try {
            await api.post(RETRY_ENDPOINT[scope](id));
            toast.success("Retry queued — refreshing");
            setTimeout(load, 800);
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    // Retryable = failed AND permanently_failed (exhausted auto-retries)
    const retryableRows = useMemo(
        () => rows.filter(r => r.status === "failed" && r.permanent_failure),
        [rows]
    );
    const retryableIds = useMemo(() => retryableRows.map(r => r.id), [retryableRows]);

    const toggleOne = (id, e) => {
        e?.stopPropagation();
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };
    const toggleAll = () => {
        if (selected.size === retryableIds.length && retryableIds.length > 0) {
            setSelected(new Set());
        } else {
            setSelected(new Set(retryableIds));
        }
    };

    const runBulk = async (payload, label) => {
        setBulkRunning(true);
        try {
            const { data } = await api.post(BULK_RETRY_ENDPOINT[scope], payload);
            const { requested = 0, succeeded = 0, failed = 0, skipped = 0 } = data || {};
            toast.success(
                `${label}: ${succeeded}/${requested} succeeded` +
                (failed ? ` · ${failed} failed` : "") +
                (skipped ? ` · ${skipped} skipped` : "")
            );
            setSelected(new Set());
            setTimeout(load, 800);
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setBulkRunning(false);
        }
    };

    const retrySelected = () => {
        if (selected.size === 0) return;
        runBulk({ ids: Array.from(selected) }, "Retry selected");
    };
    const retryAllPermanent = () => {
        if (retryableIds.length === 0) {
            toast.info("No permanently-failed rows to retry");
            return;
        }
        if (!window.confirm(`Retry ALL ${retryableIds.length} permanently-failed message(s)? This will re-attempt every exhausted send.`)) return;
        runBulk({ all_permanent_failures: true }, "Retry all failed");
    };

    const allRetryableSelected = retryableIds.length > 0 && selected.size === retryableIds.length;

    return (
        <div className="space-y-4" data-testid={`comm-audit-${scope}`}>
            <div className="flex flex-wrap gap-2 items-center text-[10px] uppercase tracking-[0.16em]">
                {["", "email", "whatsapp", "sms"].map(ch => (
                    <button key={ch} onClick={() => setFilters({ ...filters, channel: ch })}
                        data-testid={`comm-audit-ch-${ch || "all"}`}
                        className={`px-3 py-1.5 border ${filters.channel === ch ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                        {ch || "All channels"}
                    </button>
                ))}
                <div className="mx-2 w-px h-4 bg-border"></div>
                {["", "sent", "failed"].map(s => (
                    <button key={s} onClick={() => setFilters({ ...filters, status: s })}
                        data-testid={`comm-audit-st-${s || "all"}`}
                        className={`px-3 py-1.5 border ${filters.status === s ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                        {s || "All status"}
                    </button>
                ))}
                <button onClick={load} className="ml-auto text-[10px] text-muted-foreground hover:text-foreground">Refresh</button>
            </div>

            {/* Bulk retry action bar */}
            <div className="flex flex-wrap items-center gap-2 px-3 py-2 border border-border bg-background"
                data-testid="comm-audit-bulk-bar">
                <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                    {selected.size > 0
                        ? <>Selected · <span className="text-primary">{selected.size}</span></>
                        : <>Retryable (exhausted): <span className="text-primary">{retryableIds.length}</span></>}
                </span>
                <div className="ml-auto flex flex-wrap gap-2">
                    <button onClick={retrySelected}
                        disabled={selected.size === 0 || bulkRunning}
                        data-testid="comm-audit-retry-selected"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-border text-muted-foreground hover:text-primary hover:border-primary disabled:opacity-30 disabled:hover:text-muted-foreground disabled:hover:border-border">
                        {bulkRunning ? <Loader2 className="w-3 h-3 animate-spin"/> : <RotateCcw className="w-3 h-3"/>}
                        Retry selected ({selected.size})
                    </button>
                    <button onClick={retryAllPermanent}
                        disabled={retryableIds.length === 0 || bulkRunning}
                        data-testid="comm-audit-retry-all-permanent"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-30 disabled:hover:bg-transparent">
                        {bulkRunning ? <Loader2 className="w-3 h-3 animate-spin"/> : <RotateCcw className="w-3 h-3"/>}
                        Retry all permanently failed
                    </button>
                </div>
            </div>

            <div className="border border-border bg-card overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        <tr className="border-b border-border">
                            <th className="px-3 py-2 w-[36px]">
                                <input type="checkbox"
                                    checked={allRetryableSelected}
                                    onChange={toggleAll}
                                    disabled={retryableIds.length === 0}
                                    data-testid="comm-audit-select-all"
                                    className="accent-primary cursor-pointer disabled:cursor-not-allowed"/>
                            </th>
                            <th className="text-left px-3 py-2">When</th>
                            <th className="text-left px-3 py-2">Ch</th>
                            <th className="text-left px-3 py-2">To</th>
                            <th className="text-left px-3 py-2">Subject</th>
                            <th className="text-center px-3 py-2">Status</th>
                            <th className="text-left px-3 py-2">Mode</th>
                            <th className="text-left px-3 py-2">Retries</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan={8} className="px-3 py-12 text-center text-muted-foreground">
                                {loading ? "Loading…" : "No outbound messages yet."}
                            </td></tr>
                        )}
                        {rows.map(r => {
                            const Ch = CH_ICON[r.channel] || Send;
                            const ok = r.status === "sent";
                            const isOpen = expanded === r.id;
                            const retryCount = r.retry_count || 0;
                            const exhausted = r.permanent_failure;
                            const canSelect = !ok && exhausted;
                            const isSelected = selected.has(r.id);
                            return (
                                <>
                                    <tr key={r.id} className="border-b border-card hover:bg-muted/50 cursor-pointer"
                                        data-testid={`comm-audit-row-${r.id}`}
                                        onClick={() => setExpanded(isOpen ? null : r.id)}>
                                        <td className="px-3 py-2" onClick={(e) => canSelect && toggleOne(r.id, e)}>
                                            {canSelect && (
                                                <input type="checkbox" checked={isSelected}
                                                    onChange={(e) => toggleOne(r.id, e)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    data-testid={`comm-audit-select-${r.id}`}
                                                    className="accent-primary cursor-pointer"/>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmtDateTime(r.sent_at)}</td>
                                        <td className="px-3 py-2"><Ch className="w-3.5 h-3.5 text-primary"/></td>
                                        <td className="px-3 py-2 text-foreground truncate max-w-[180px]">{r.to}</td>
                                        <td className="px-3 py-2 text-muted-foreground truncate max-w-[200px]">{r.subject || "—"}</td>
                                        <td className="px-3 py-2 text-center">
                                            {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 inline"/> : <AlertCircle className="w-3.5 h-3.5 text-rose-400 inline"/>}
                                        </td>
                                        <td className="px-3 py-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{r.mode || "—"}</td>
                                        <td className="px-3 py-2 text-[10px] uppercase tracking-[0.16em]">
                                            {ok ? <span className="text-muted-foreground">—</span>
                                                : exhausted
                                                    ? <button onClick={(e) => retry(r.id, e)} data-testid={`comm-audit-retry-${r.id}`}
                                                        className="inline-flex items-center gap-1 text-primary hover:underline">
                                                        <RotateCcw className="w-3 h-3"/>Retry now
                                                      </button>
                                                    : <span className="text-amber-400">Retrying · {retryCount}/3</span>}
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="bg-background">
                                            <td colSpan={8} className="px-3 py-3">
                                                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Body</div>
                                                <pre className="whitespace-pre-wrap text-xs text-foreground font-sans">{r.body || "(no body)"}</pre>
                                                {r.provider_response?.error && (
                                                    <div className="mt-2 text-[10px] text-rose-400 font-mono">Error: {r.provider_response.error}</div>
                                                )}
                                                {r.delivered_at && (
                                                    <div className="mt-2 text-[10px] text-emerald-400">Delivered at {fmtDateTime(r.delivered_at)}</div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
